import { computed } from 'vue';
import { IPresentableEntity } from '@/models/available-entities.interface';
import { CampaignChange, CampaignChangeEnum, CampaignUnlinkOrganisationsChange, MinimalOrganisationDto }
	from '@studyportals/campaign-management-api-interface';
import store from '@/store';

class UnlinkOrganisationWarningPresenter {

	private entitiesAffectedByUnlinking = computed((): IPresentableEntity[] => {
		return store.getters.entitiesAffectedByUnlinking();
	});

	private availableEntities = computed((): IPresentableEntity[] => {
		return store.getters.availableEntities();
	});

	private linkedOrganisations = computed((): MinimalOrganisationDto[] => {
		return store.getters.linkedOrganisations();
	});

	public canSaveProceedBasedOnUnlinkedOrganisationsData(changes: CampaignChange[]): boolean {
		if (!this.availableEntities.value.length) {
			return true;
		}

		const unlinkingChange = changes.find((change) => change.type === CampaignChangeEnum.UNLINK_ORGANISATIONS);
		if (!unlinkingChange) {
			return true;
		}

		// If entities were already stored as affected by unlinking, the user has pressed "proceed" inside the warning modal.
		if (this.entitiesAffectedByUnlinking.value.length !== 0) {
			return true;
		}

		const affectedEntities = this.determineEntitiesAffectedByUnlinking(unlinkingChange as CampaignUnlinkOrganisationsChange);
		store.mutations.setEntitiesAffectedByUnlinking(affectedEntities);
		return affectedEntities.length === 0;
	}

	private determineEntitiesAffectedByUnlinking(unlinkingChange: CampaignUnlinkOrganisationsChange): IPresentableEntity[] {
		return this.availableEntities.value.filter(
			(entity) => unlinkingChange.value.find(
				(id) => this.isEntityAffectedByUnlinkedId(entity, unlinkingChange.value, id)
			)
		);
	}

	private isEntityAffectedByUnlinkedId(entity: IPresentableEntity, allUnlinkedIds: number[], unlinkedId: number): boolean {
		const idOfParentThatWillRemainLinked = this.linkedOrganisations.value
			.map((organisation) => organisation.id)
			.filter((id) => !allUnlinkedIds.includes(id))
			.find((id) => this.doesEntityRelateToOrganisationId(entity, id));

		// An affected entity should be attached and none of its (grand)parents should be linked after this change.
		if (!entity.attached || idOfParentThatWillRemainLinked !== undefined) {
			return false;
		}

		return this.doesEntityRelateToOrganisationId(entity, unlinkedId);
	}

	private doesEntityRelateToOrganisationId(entity: IPresentableEntity, organisationId: number): boolean {
		// The entity either has the same id as the organisation.
		if (entity.id === organisationId) {
			return true;
		}

		// Or it's a (grand)child of the organisation.
		return entity.relevantParentIndexes.find((index) => this.availableEntities.value[index].id === organisationId) !== undefined;
	}
}

export default new UnlinkOrganisationWarningPresenter();
